<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { ROLE_MODULE } from "@/state/modules/role";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { roleMethods, lookupMethods } from "@/state/helpers";

export default {
  page() {
    return {
      title: this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.role")])
        : i18n.t("label.createX", [i18n.t("module.role")]),
    };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      form: { name: "", permissionCodes: [] },
      submitting: false,
    };
  },
  validations: { form: { name: { required } } },
  computed: {
    ...mapState(ROLE_MODULE, ["details"]),
    ...mapState(LOOKUP_MODULE, ["permissions"]),
    isEdit() {
      return !!this.$route.params.id;
    },
    title() {
      return this.isEdit
        ? i18n.t("label.updateX", [i18n.t("module.role")])
        : i18n.t("label.createX", [i18n.t("module.role")]);
    },
    items() {
      return [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.role"), href: "/roles" },
        { text: this.title, active: true },
      ];
    },
  },
  async created() {
    await this.lookupPermission();
    if (this.isEdit) {
      const result = await this.getRole({ id: this.$route.params.id });
      const d = result?.data || {};
      this.form.name = d.name || "";
      this.form.permissionCodes = (d.permissions || []).map((p) => p.code);
    }
  },
  methods: {
    ...roleMethods,
    ...lookupMethods,
    isChecked(code) {
      return this.form.permissionCodes.includes(code);
    },
    togglePermission(code, checked) {
      if (checked && !this.form.permissionCodes.includes(code)) {
        this.form.permissionCodes.push(code);
      } else if (!checked) {
        this.form.permissionCodes = this.form.permissionCodes.filter((c) => c !== code);
      }
    },
    moduleCodes(group) {
      return (group.permissions || []).map((p) => p.code);
    },
    isModuleAllChecked(group) {
      const codes = this.moduleCodes(group);
      return codes.length > 0 && codes.every((c) => this.form.permissionCodes.includes(c));
    },
    isModuleIndeterminate(group) {
      const codes = this.moduleCodes(group);
      const selected = codes.filter((c) => this.form.permissionCodes.includes(c)).length;
      return selected > 0 && selected < codes.length;
    },
    toggleModule(group, checked) {
      const codes = this.moduleCodes(group);
      if (checked) {
        const set = new Set([...this.form.permissionCodes, ...codes]);
        this.form.permissionCodes = [...set];
      } else {
        this.form.permissionCodes = this.form.permissionCodes.filter((c) => !codes.includes(c));
      }
    },
    allCodes() {
      return (this.permissions || []).reduce((acc, g) => acc.concat(this.moduleCodes(g)), []);
    },
    selectAll() {
      this.form.permissionCodes = this.allCodes();
    },
    unselectAll() {
      this.form.permissionCodes = [];
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.submitting = true;
      try {
        if (this.isEdit) {
          await this.updateRole({
            id: this.$route.params.id,
            data: this.form,
            filter: {},
          });
        } else {
          await this.createRole({ data: this.form, filter: {} });
        }
        this.$router.push({ name: "Roles-listing" });
      } finally {
        this.submitting = false;
      }
    },
    cancel() {
      this.$router.push({ name: "Roles-listing" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-form-group :label="$t('label.name')">
                <b-form-input v-model="form.name" :state="$v.form.name.$dirty ? !$v.form.name.$error : null" />
              </b-form-group>

              <div class="d-flex align-items-center mt-4 mb-3">
                <h5 class="mb-0">{{ $t('label.permissions') }}</h5>
                <div class="ml-auto">
                  <b-button variant="outline-primary" size="sm" class="mr-2" @click="selectAll">{{ $t('label.selectAll') }}</b-button>
                  <b-button variant="outline-secondary" size="sm" @click="unselectAll">{{ $t('label.unselectAll') }}</b-button>
                </div>
              </div>
              <div v-for="group in permissions" :key="group.module" class="mb-3 p-3 border rounded">
                <b-form-checkbox
                  class="mb-2"
                  :checked="isModuleAllChecked(group)"
                  :indeterminate="isModuleIndeterminate(group)"
                  @change="(v) => toggleModule(group, v)"
                >
                  <span class="text-primary font-weight-bold">{{ group.module }}</span>
                </b-form-checkbox>
                <b-row>
                  <b-col cols="6" md="4" lg="3" v-for="p in group.permissions" :key="p.code">
                    <b-form-checkbox :checked="isChecked(p.code)" @change="(v) => togglePermission(p.code, v)">
                      {{ p.name }}
                    </b-form-checkbox>
                  </b-col>
                </b-row>
              </div>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
